import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  Text,
  Tooltip,
  mergeClasses,
} from "@fluentui/react-components";
import {
  ArrowDownload24Regular,
  Delete24Regular,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import {
  addWorkspaceLibraryDocument,
  deleteWorkspaceLibraryDocument,
  getWorkspaceLibraryDocuments,
} from "api";
import { ALLOWED_FILE_EXTENSIONS, ConstantValues } from "common/constants";
import {
  downloadWorkspaceLibraryDocument,
  safeDecodeURIComponent,
  uploadFilesToStorage,
} from "utils/blobUtils";
import { ActionDialog } from "../../actionDialog/ActionDialog";
import { DocumentIcon } from "../../../icons/DocumentIcon";
import { FileDropzone } from "../../fileDropzone/FileDropzone";
import { IWorkspaceLibraryDocument } from "../workspaceLibrary/workspaceLibrary.types";
import { useStyles } from "./AddDocumentsDialog.styles";
import {
  useSharedDialogContentStyles,
  useSharedFileCardStyles,
} from "theme/sharedStyles";
import {
  IAddDocumentsDialogProps,
  IUploadingFile,
  DIALOG_LABELS,
  DELETE_CONFIRM,
} from "./AddDocumentsDialog.types";

export const AddDocumentsDialog = (
  props: IAddDocumentsDialogProps,
): JSX.Element => {
  const styles = useStyles();
  const sharedContentStyles = useSharedDialogContentStyles();
  const fileCardStyles = useSharedFileCardStyles();
  const [uploadingFiles, setUploadingFiles] = useState<IUploadingFile[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<
    IWorkspaceLibraryDocument[]
  >([]);
  const [deletingFilenames, setDeletingFilenames] = useState<Set<string>>(
    new Set(),
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmDoc, setDeleteConfirmDoc] =
    useState<IWorkspaceLibraryDocument | null>(null);

  useEffect(() => {
    if (props.isOpen && props.workspace.id) {
      fetchExistingDocuments();
    }
  }, [props.isOpen, props.workspace.id]);

  const fetchExistingDocuments = async () => {
    try {
      setIsLoading(true);
      const docs = await getWorkspaceLibraryDocuments({
        workspaceId: props.workspace.id,
      });
      setExistingDocuments(docs || []);
    } catch (error) {
      console.error("Failed to fetch workspace documents:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const allowedExtensions = ALLOWED_FILE_EXTENSIONS.map((ext) =>
    ext.toLowerCase(),
  );

  const validateFile = (file: File): string | null => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (fileExtension === "pptx" || fileExtension === "ppt") {
      return "PPT/PPTX files are not supported. Please save the file as a PDF and then upload the PDF.";
    }

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      return `The file "${file.name}" is not supported. Allowed types: ${ALLOWED_FILE_EXTENSIONS.join(", ")}`;
    }

    return null;
  };

  const uploadFiles = async (files: File[]) => {
    setErrorMessage("");

    const validFiles: File[] = [];
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        setErrorMessage(error);
        return;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    const newFiles: IUploadingFile[] = validFiles.map((file) => ({
      id: crypto.randomUUID(),
      file,
      originalFilename: file.name,
      status: "uploading" as const,
    }));

    setUploadingFiles((prev) => [...prev, ...newFiles]);

    for (const uploadFile of newFiles) {
      try {
        const uploaded = await uploadFilesToStorage(
          [{ id: uploadFile.id, file: uploadFile.file }],
          "workspace_library",
          props.workspace.id,
          false,
        );

        if (uploaded.length > 0) {
          const blobFilename = safeDecodeURIComponent(
            uploaded[0].url?.split("/").pop() || ConstantValues.EMPTY_STRING,
          );

          await addWorkspaceLibraryDocument({
            workspaceId: props.workspace.id,
            filename: blobFilename,
            originalFilename: uploadFile.file.name,
          });

          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === uploadFile.id
                ? { ...f, status: "uploaded" as const, blobFilename }
                : f,
            ),
          );
        }
      } catch (error) {
        console.error("Failed to upload file:", error);
        setUploadingFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id ? { ...f, status: "error" as const } : f,
          ),
        );
        setErrorMessage(
          `Failed to upload "${uploadFile.originalFilename}". Please try again.`,
        );
      }
    }
  };

  const handleRemoveUploadedFile = (fileId: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleDeleteClick = (doc: IWorkspaceLibraryDocument) => {
    setDeleteConfirmDoc(doc);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmDoc) return;
    const doc = deleteConfirmDoc;
    setDeleteConfirmDoc(null);
    setDeletingFilenames((prev) => new Set(prev).add(doc.filename));
    try {
      await deleteWorkspaceLibraryDocument({
        workspaceId: props.workspace.id,
        filename: doc.filename,
      });
      setExistingDocuments((prev) =>
        prev.filter((d) => d.filename !== doc.filename),
      );
    } catch (error) {
      console.error("Failed to delete document:", error);
      setErrorMessage("Failed to delete document. Please try again.");
    } finally {
      setDeletingFilenames((prev) => {
        const next = new Set(prev);
        next.delete(doc.filename);
        return next;
      });
    }
  };

  const handleDownloadDocument = async (doc: IWorkspaceLibraryDocument) => {
    try {
      await downloadWorkspaceLibraryDocument(props.workspace.id, doc);
    } catch (error) {
      console.error("Failed to download document:", error);
      setErrorMessage("Failed to download document. Please try again.");
    }
  };

  const handleClose = () => {
    setUploadingFiles([]);
    setErrorMessage("");
    props.onClose();
  };

  const hasActiveUploads = uploadingFiles.some((f) => f.status === "uploading");

  const getStatusText = (status: IUploadingFile["status"]) => {
    switch (status) {
      case "uploading":
        return DIALOG_LABELS.UPLOADING;
      case "uploaded":
        return DIALOG_LABELS.UPLOADED;
      case "error":
        return DIALOG_LABELS.ERROR;
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <Dialog
        open={props.isOpen}
        modalType="modal"
        onOpenChange={(_, data) => {
          if (!data.open) handleClose();
        }}
      >
        <DialogSurface className={styles.modalContent}>
          <DialogContent className={mergeClasses(styles.content)}>
            <div className={styles.titleContainer}>
              <DialogTitle>{DIALOG_LABELS.TITLE}</DialogTitle>
              <Button
                icon={<Dismiss24Regular />}
                appearance="subtle"
                size="small"
                onClick={handleClose}
                disabled={hasActiveUploads}
                aria-label="Close dialog"
              />
            </div>

            <DialogBody
              className={mergeClasses(
                styles.body,
                sharedContentStyles.formContainer,
              )}
            >
              {isLoading && (
                <div
                  className={sharedContentStyles.formLoadingOverlay}
                  role="status"
                  aria-live="polite"
                >
                  <Spinner size="small" />
                </div>
              )}
              <FileDropzone
                onFilesSelected={(files) => uploadFiles(files)}
                acceptedExtensions={ALLOWED_FILE_EXTENSIONS.map((ext) =>
                  ext.toLowerCase(),
                )}
                multiple
                disabled={isLoading}
                onValidationError={(msg) => setErrorMessage(msg)}
              />

              {errorMessage && (
                <div className={sharedContentStyles.feedbackErrorBox}>
                  {errorMessage}
                </div>
              )}

              {(uploadingFiles.length > 0 || existingDocuments.length > 0) && (
                <div
                  className={mergeClasses(
                    fileCardStyles.fileList,
                    styles.documentList,
                  )}
                  role="list"
                  aria-label="Uploaded documents"
                >
                  {uploadingFiles.map((uploadFile) => {
                    const isUploading = uploadFile.status === "uploading";
                    return (
                      <div
                        key={uploadFile.id}
                        role="listitem"
                        className={mergeClasses(
                          fileCardStyles.fileCard,
                          isUploading && styles.fileItemDisabled,
                        )}
                      >
                        <span className={fileCardStyles.fileIcon}>
                          <DocumentIcon size={20} />
                        </span>
                        <div className={fileCardStyles.fileInfo}>
                          <Tooltip
                            content={uploadFile.originalFilename}
                            relationship="description"
                          >
                            <Text className={fileCardStyles.fileName}>
                              {uploadFile.originalFilename}
                            </Text>
                          </Tooltip>
                          {uploadFile.status !== "uploaded" && (
                            <Text className={fileCardStyles.fileStatus}>
                              {getStatusText(uploadFile.status)}
                            </Text>
                          )}
                        </div>
                        {isUploading ? (
                          <Spinner size="tiny" className={styles.fileSpinner} />
                        ) : (
                          <div className={fileCardStyles.fileActions}>
                            <Tooltip content="Remove" relationship="label">
                              <Button
                                icon={<Delete24Regular />}
                                appearance="subtle"
                                size="small"
                                className={fileCardStyles.fileDeleteButton}
                                onClick={() =>
                                  handleRemoveUploadedFile(uploadFile.id)
                                }
                              />
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {existingDocuments.map((doc) => {
                    const isDeleting = deletingFilenames.has(doc.filename);
                    return (
                      <div
                        key={doc.filename}
                        role="listitem"
                        className={mergeClasses(
                          fileCardStyles.fileCard,
                          isDeleting && styles.fileItemDisabled,
                        )}
                      >
                        <span className={fileCardStyles.fileIcon}>
                          <DocumentIcon size={20} />
                        </span>
                        <div className={fileCardStyles.fileInfo}>
                          <Tooltip
                            content={doc.originalFilename}
                            relationship="description"
                          >
                            <Text className={fileCardStyles.fileName}>
                              {doc.originalFilename}
                            </Text>
                          </Tooltip>
                          {isDeleting && (
                            <Text className={fileCardStyles.fileStatus}>
                              {DIALOG_LABELS.DELETING}
                            </Text>
                          )}
                        </div>
                        {isDeleting ? (
                          <Spinner size="tiny" className={styles.fileSpinner} />
                        ) : (
                          <div className={fileCardStyles.fileActions}>
                            <Tooltip content="Download" relationship="label">
                              <Button
                                icon={<ArrowDownload24Regular />}
                                appearance="subtle"
                                size="small"
                                className={fileCardStyles.fileDownloadButton}
                                onClick={() => handleDownloadDocument(doc)}
                              />
                            </Tooltip>
                            {props.canDelete !== false && (
                              <Tooltip content="Delete" relationship="label">
                                <Button
                                  icon={<Delete24Regular />}
                                  appearance="subtle"
                                  size="small"
                                  className={fileCardStyles.fileDeleteButton}
                                  onClick={() => handleDeleteClick(doc)}
                                />
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </DialogBody>

            <DialogActions className={styles.footer}>
              <Button
                appearance="primary"
                onClick={handleClose}
                disabled={hasActiveUploads}
              >
                {DIALOG_LABELS.DONE}
              </Button>
            </DialogActions>
          </DialogContent>
        </DialogSurface>
      </Dialog>

      {deleteConfirmDoc && (
        <ActionDialog
          isVisible={true}
          title={DELETE_CONFIRM.TITLE}
          message={DELETE_CONFIRM.MESSAGE(deleteConfirmDoc.originalFilename)}
          onConfirmation={handleConfirmDelete}
          onDismiss={() => setDeleteConfirmDoc(null)}
          confirmationMessage={DELETE_CONFIRM.ACTION}
          dismissMessage={DELETE_CONFIRM.DISMISS}
        />
      )}
    </div>
  );
};
